import type { CSSProperties } from "react";

/**
 * Hatchery brand mark (V3, light variant — vector from V3-appicon-light, inlined).
 * The app-icon's white rounded tile is dropped so this matches the bare
 * Carbonsix/Sigmakit marks (transparent background). Native art box is 120×120.
 *
 * `mono` keeps the whole mark identical EXCEPT the center cell's brand red, which
 * follows `currentColor` — so the accent can be themed/tinted. Everything else
 * (the white/gray cells and dark outlines) is unchanged.
 */

// Tight square frame around the raw glyph (bbox x[21.89,98.11] y[16,90.5]) so the
// mark fills the icon like Carbonsix/Sigmakit — no app-icon inset/padding.
const HATCHERY_VIEWBOX = "19 12.25 82 82";
const OUTLINE = "#16181d";

// Each cell = a shadow polygon (drawn first) + a face polygon offset 3px up.
const SHADOW_WIDTH = 2.2;
const CELLS = [
  {
    shadow: "60,19 69.53,24.5 69.53,35.5 60,41 50.47,35.5 50.47,24.5",
    face: "60,16 69.53,21.5 69.53,32.5 60,38 50.47,32.5 50.47,21.5",
    shadowFill: "#d2d3d9",
    faceFill: "#ffffff",
    faceWidth: 2.4,
    brand: false,
  },
  {
    shadow: "88.58,68.5 98.11,74 98.11,85 88.58,90.5 79.05,85 79.05,74",
    face: "88.58,65.5 98.11,71 98.11,82 88.58,87.5 79.05,82 79.05,71",
    shadowFill: "#d2d3d9",
    faceFill: "#ffffff",
    faceWidth: 2.4,
    brand: false,
  },
  {
    shadow: "31.42,68.5 40.95,74 40.95,85 31.42,90.5 21.89,85 21.89,74",
    face: "31.42,65.5 40.95,71 40.95,82 31.42,87.5 21.89,82 21.89,71",
    shadowFill: "#d2d3d9",
    faceFill: "#ffffff",
    faceWidth: 2.4,
    brand: false,
  },
  {
    // center "egg" — the brand-red cell.
    shadow: "60,48 72.99,55.5 72.99,70.5 60,78 47.01,70.5 47.01,55.5",
    face: "60,45 72.99,52.5 72.99,67.5 60,75 47.01,67.5 47.01,52.5",
    shadowFill: "#b02828",
    faceFill: "#ff4b4b",
    faceWidth: 2.6,
    brand: true,
  },
];

export interface HatcheryIconProps {
  size?: number;
  /** Make the center brand cell follow `currentColor` instead of the fixed red. */
  mono?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function HatcheryIcon({ size = 24, mono = false, className, style }: HatcheryIconProps) {
  return (
    <svg
      className={className}
      style={style}
      width={size}
      height={size}
      viewBox={HATCHERY_VIEWBOX}
      role="img"
      aria-label="Hatchery"
    >
      <g strokeLinejoin="round" strokeLinecap="round" stroke={OUTLINE}>
        {CELLS.map((cell, i) => {
          const tinted = mono && cell.brand;
          return (
            <g key={i}>
              <polygon
                points={cell.shadow}
                fill={tinted ? "currentColor" : cell.shadowFill}
                strokeWidth={SHADOW_WIDTH}
              />
              <polygon
                points={cell.face}
                fill={tinted ? "currentColor" : cell.faceFill}
                strokeWidth={cell.faceWidth}
              />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

HatcheryIcon.displayName = "HatcheryIcon";
